import { differenceInDays, fromUnixTime, isAfter, isBefore } from 'date-fns';
import { getFormattedShopifyId } from 'shared/utils/shopify/ids';
import { useQueryGetOrdersV2 } from 'state/internal/getOrders';

// Returns the highest priority upcoming order from customer data
export function useGetPrioritizedUpcomingOrderV2(maxDays = 0, subscriptionId?: string) {
  const { data, isFetching } = useQueryGetOrdersV2();

  if (!data || isFetching) return null;

  const eligibleOrders = data.filter((order) => {
    const active = order.status !== 'CANCELLED';

    const isOrderRelatedToCurrentSub =
      subscriptionId &&
      order.shopify_contract_id === getFormattedShopifyId('SubscriptionContract', subscriptionId);

    const orderDate = fromUnixTime(Number(order.order_date));
    const withinTimeLimit = maxDays > 0 ? differenceInDays(new Date(), orderDate) < maxDays : true;

    return active && isOrderRelatedToCurrentSub && withinTimeLimit;
  });

  if (!eligibleOrders.length) return null;

  const prioritizedUpcomingOrder = eligibleOrders.reduce((highestPriorityOrder, currentOrder) => {
    const currentShipmentStatus = currentOrder.status.toUpperCase();
    const currentOrderDate = fromUnixTime(Number(currentOrder.order_date));
    const priorityOrderDate = fromUnixTime(Number(highestPriorityOrder.order_date));
    const priorityShipmentStatus = highestPriorityOrder.status;

    // Prioritize earliest undelivered
    if (
      currentShipmentStatus !== 'FULFILLED' &&
      (priorityShipmentStatus === 'FULFILLED' || isBefore(currentOrderDate, priorityOrderDate))
    ) {
      return currentOrder;
    }

    // Prioritize latest delivered
    if (
      currentShipmentStatus === 'FULFILLED' &&
      priorityShipmentStatus === 'FULFILLED' &&
      isAfter(currentOrderDate, priorityOrderDate)
    ) {
      return currentOrder;
    }

    return highestPriorityOrder;
  });

  return prioritizedUpcomingOrder;
}
